// Session lifecycle helpers.
//
// Pentester findings addressed here:
//  - Child windows opened by the app must be closed on sign-off.
//  - Signing off in one window/tab must terminate the session everywhere
//    (session inactivity termination across all same-origin windows).

const LOGOUT_CHANNEL = 'adsign-auth';
const LOGOUT_KEY = 'adsign:logout-broadcast';

// Registry of child windows this window opened via openChildWindow().
const childWindows = new Set();

let channel = null;
const getChannel = () => {
  if (channel) return channel;
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(LOGOUT_CHANNEL);
  }
  return channel;
};

// Open a child window and track its handle so it can be force-closed on logout.
// Use this instead of window.open where the app spawns popups/previews.
export const openChildWindow = (url, target = '_blank', features) => {
  const w = window.open(url, target, features);
  if (w) childWindows.add(w);
  return w;
};

// Close every tracked child window that is still open.
export const closeChildWindows = () => {
  childWindows.forEach((w) => {
    try {
      if (w && !w.closed) w.close();
    } catch (_e) {
      // Cross-origin / already-closed — nothing we can do, drop it.
    }
  });
  childWindows.clear();
};

// Tell all other same-origin windows/tabs to terminate their session now.
export const broadcastLogout = () => {
  const ch = getChannel();
  if (ch) {
    try {
      ch.postMessage({ type: 'logout', at: Date.now() });
    } catch (_e) {}
  }
  // storage-event fallback for browsers without BroadcastChannel.
  try {
    localStorage.setItem(LOGOUT_KEY, String(Date.now()));
    localStorage.removeItem(LOGOUT_KEY);
  } catch (_e) {}
};

// Wire a window so it reacts to a logout broadcast from any other window:
// close its own child windows and hand control back to `onRemoteLogout`
// (e.g. redirect to /login). Returns an unsubscribe function.
export const listenForLogout = (onRemoteLogout) => {
  const handle = () => {
    closeChildWindows();
    if (typeof onRemoteLogout === 'function') onRemoteLogout();
  };

  const ch = getChannel();
  const onMessage = (e) => {
    if (e && e.data && e.data.type === 'logout') handle();
  };
  const onStorage = (e) => {
    if (e.key === LOGOUT_KEY && e.newValue) handle();
  };

  if (ch) ch.addEventListener('message', onMessage);
  window.addEventListener('storage', onStorage);

  return () => {
    if (ch) ch.removeEventListener('message', onMessage);
    window.removeEventListener('storage', onStorage);
  };
};
